/**
 * GS1 element-string parsing, shared by every scan source in the warehouse app.
 *
 * The camera (Scandit) and a handheld laser/imager (a Zebra device running DataWedge in
 * keystroke mode) read the *same* barcode but do not always hand us the same bytes, so all
 * payload interpretation lives here rather than next to either transport:
 *
 *   - Scandit returns the decoded data with FNC1 rendered as GS (0x1D) and no symbology
 *     identifier.
 *   - DataWedge keystroke output also renders FNC1 as GS, but only reaches us intact when the
 *     data lands in a focused field; delivered purely as key events the control characters can
 *     be dropped. Some profiles also prefix an AIM identifier (`]C1`, `]d2`), rewrite GS as a
 *     printable token through Advanced Data Formatting, or wrap the data in an ISO 15434
 *     envelope.
 *
 * So the parser is built to survive a *missing* separator rather than to assume one. Field
 * boundaries come from the GS1 Barcode Syntax Dictionary (data lengths per Application
 * Identifier); where a variable-length field cannot be delimited by length alone we search for
 * the reading that makes the rest of the payload validate — see `parseGs1`.
 */

/** ASCII 29. FNC1 in a GS1 barcode is transmitted as this by every decoder we deal with. */
export const GS = '\u001d';

/** ASCII 30/4. Only ever seen as the framing of an ISO 15434 envelope, never as data. */
const RS = '\u001e';
const EOT = '\u0004';

export type AiSpec = {
  ai: string;
  /** Minimum data length, excluding the AI itself. */
  min: number;
  /** Maximum data length, excluding the AI itself. */
  max: number;
  /** GS1 "pre-defined length" AI: legal to concatenate without a following FNC1. */
  predefined: boolean;
  /** Every component of the data field is numeric. */
  numeric: boolean;
};

/**
 * Derived verbatim from the GS1 Barcode Syntax Dictionary (gs1/gs1-syntax-dictionary), encoded
 * as `AI[-AI]=min:max:predefined:numeric` to keep 541 AI definitions to a few hundred bytes.
 * Regenerate from the dictionary rather than hand-editing.
 */
const AI_TABLE_SOURCE =
  '00=18:18:1:1 01-03=14:14:1:1 10=1:20:0:0 11-13=6:6:1:1 15-17=6:6:1:1 20=2:2:1:1 ' +
  '21-22=1:20:0:0 235=1:28:0:0 240-241=1:30:0:0 242=1:6:0:1 243=1:20:0:0 250-251=1:30:0:0 ' +
  '253=13:30:0:0 254=1:20:0:0 255=13:25:0:1 30=1:8:0:1 3100-3105=6:6:1:1 3110-3115=6:6:1:1 ' +
  '3120-3125=6:6:1:1 3130-3135=6:6:1:1 3140-3145=6:6:1:1 3150-3155=6:6:1:1 ' +
  '3160-3165=6:6:1:1 3200-3205=6:6:1:1 3210-3215=6:6:1:1 3220-3225=6:6:1:1 ' +
  '3230-3235=6:6:1:1 3240-3245=6:6:1:1 3250-3255=6:6:1:1 3260-3265=6:6:1:1 ' +
  '3270-3275=6:6:1:1 3280-3285=6:6:1:1 3290-3295=6:6:1:1 3300-3305=6:6:1:1 ' +
  '3310-3315=6:6:1:1 3320-3325=6:6:1:1 3330-3335=6:6:1:1 3340-3345=6:6:1:1 ' +
  '3350-3355=6:6:1:1 3360-3365=6:6:1:1 3370-3375=6:6:1:1 3400-3405=6:6:1:1 ' +
  '3410-3415=6:6:1:1 3420-3425=6:6:1:1 3430-3435=6:6:1:1 3440-3445=6:6:1:1 ' +
  '3450-3455=6:6:1:1 3460-3465=6:6:1:1 3470-3475=6:6:1:1 3480-3485=6:6:1:1 ' +
  '3490-3495=6:6:1:1 3500-3505=6:6:1:1 3510-3515=6:6:1:1 3520-3525=6:6:1:1 ' +
  '3530-3535=6:6:1:1 3540-3545=6:6:1:1 3550-3555=6:6:1:1 3560-3565=6:6:1:1 ' +
  '3570-3575=6:6:1:1 3600-3605=6:6:1:1 3610-3615=6:6:1:1 3620-3625=6:6:1:1 ' +
  '3630-3635=6:6:1:1 3640-3645=6:6:1:1 3650-3655=6:6:1:1 3660-3665=6:6:1:1 ' +
  '3670-3675=6:6:1:1 3680-3685=6:6:1:1 3690-3695=6:6:1:1 37=1:8:0:1 3900-3909=1:15:0:1 ' +
  '3910-3919=4:18:0:1 3920-3929=1:15:0:1 3930-3939=4:18:0:1 3940-3943=4:4:0:1 ' +
  '3950-3955=6:6:0:1 400-401=1:30:0:0 402=17:17:0:1 403=1:30:0:0 410-417=13:13:1:1 ' +
  '420=1:20:0:0 421=4:12:0:0 422=3:3:0:1 423=3:15:0:1 424=3:3:0:1 425=3:15:0:1 426=3:3:0:1 ' +
  '427=1:3:0:0 4300-4301=1:35:0:0 4302-4306=1:70:0:0 4307=2:2:0:0 4308=1:30:0:0 ' +
  '4309=20:20:0:1 4310-4311=1:35:0:0 4312-4316=1:70:0:0 4317=2:2:0:0 4318=1:20:0:0 ' +
  '4319=1:30:0:0 4320=1:35:0:0 4321-4323=1:1:0:1 4324-4325=10:10:0:1 4326=6:6:0:1 ' +
  '4330-4333=6:7:0:0 7001=13:13:0:1 7002=1:30:0:0 7003=10:10:0:1 7004=1:4:0:1 7005=1:12:0:0 ' +
  '7006=6:6:0:1 7007=6:12:0:1 7008=1:3:0:0 7009=1:10:0:0 7010=1:2:0:0 7011=6:10:0:1 ' +
  '7020-7022=1:20:0:0 7023=1:30:0:0 7030-7039=4:30:0:0 7040=4:4:0:0 7041=1:4:0:0 ' +
  '710-717=1:20:0:0 7230-7239=3:30:0:0 7240=1:20:0:0 7241=2:2:0:1 7242=1:25:0:0 ' +
  '7250=8:8:0:1 7251=12:12:0:1 7252=1:1:0:1 7253-7254=1:40:0:0 7255=1:10:0:0 7256=1:90:0:0 ' +
  '7257=1:70:0:0 7259=1:40:0:0 7258=3:3:0:0 8001=14:14:0:1 8002=1:20:0:0 8003=14:30:0:0 ' +
  '8004=1:30:0:0 8005=6:6:0:1 8006=18:18:0:1 8007=1:34:0:0 8008=8:12:0:1 8009=1:50:0:0 ' +
  '8010=1:30:0:0 8011=1:12:0:1 8012=1:20:0:0 8013-8014=1:25:0:0 8017-8018=18:18:0:1 ' +
  '8019=1:10:0:1 8020=1:25:0:0 8026=18:18:0:1 8030=1:90:0:0 8040-8041=15:15:0:1 ' +
  '8042=32:32:0:1 8043=18:20:0:1 8110=1:70:0:0 8111=4:4:0:1 8112=1:70:0:0 8200=1:70:0:0 ' +
  '90=1:30:0:0 91-99=1:90:0:0';

const buildAiTable = () => {
  const specs = new Map<string, AiSpec>();
  /** First two digits of an AI determine its total length — GS1 guarantees this is unambiguous. */
  const lengthByPrefix = new Map<string, number>();

  for (const entry of AI_TABLE_SOURCE.split(' ')) {
    if (!entry) continue;
    const [range, definition] = entry.split('=');
    const [min, max, predefined, numeric] = definition.split(':');
    const [lo, hi] = range.includes('-') ? range.split('-') : [range, range];
    const width = lo.length;
    for (let value = Number(lo); value <= Number(hi); value += 1) {
      const ai = String(value).padStart(width, '0');
      specs.set(ai, {
        ai,
        min: Number(min),
        max: Number(max),
        predefined: predefined === '1',
        numeric: numeric === '1'
      });
      lengthByPrefix.set(ai.slice(0, 2), width);
    }
  }

  return { specs, lengthByPrefix };
};

const { specs: AI_SPECS, lengthByPrefix: AI_LENGTH_BY_PREFIX } = buildAiTable();

export const aiSpec = (ai: string): AiSpec | undefined => AI_SPECS.get(ai);

/** AIs whose data field is a bare YYMMDD, so a wrong boundary shows up as an impossible date. */
const YYMMDD_AIS = new Set(['11', '12', '13', '15', '16', '17', '4326', '7006']);

/**
 * AIs carrying a GS1 mod-10 check digit, and how many leading characters it covers. A passing
 * check digit is the strongest single signal that a boundary was placed correctly.
 */
const CHECK_DIGIT_AIS = new Map<string, number>([
  ['00', 18],
  ['01', 14],
  ['02', 14],
  ['03', 14],
  ['253', 13],
  ['255', 13],
  ['402', 17],
  ['410', 13],
  ['411', 13],
  ['412', 13],
  ['413', 13],
  ['414', 13],
  ['415', 13],
  ['416', 13],
  ['417', 13],
  ['8006', 14],
  ['8017', 18],
  ['8018', 18],
  ['8026', 14]
]);

/**
 * GS1 standard mod-10 check digit: weight the payload 3,1,3,1… from the right, and the check
 * digit is whatever brings the total to a multiple of ten.
 */
export const hasValidGs1CheckDigit = (digits: string): boolean => {
  if (!/^\d{2,}$/.test(digits)) return false;
  let sum = 0;
  for (let i = digits.length - 2, weight = 3; i >= 0; i -= 1, weight = weight === 3 ? 1 : 3) {
    sum += Number(digits[i]) * weight;
  }
  return (10 - (sum % 10)) % 10 === Number(digits[digits.length - 1]);
};

/**
 * A GS1 YYMMDD date as an ISO calendar date. Per the General Specifications a day of `00` means
 * "the last day of that month", which is a real and common encoding on pharmaceutical packs —
 * expiries are usually printed as a month. Returns null for anything that is not a real date, so
 * a mis-parsed field can be told apart from a genuine one.
 */
export const gs1DateToIso = (yymmdd: string | null | undefined): string | null => {
  if (!yymmdd || !/^\d{6}$/.test(yymmdd)) return null;
  const year = 2000 + Number(yymmdd.slice(0, 2));
  const month = Number(yymmdd.slice(2, 4));
  const day = Number(yymmdd.slice(4, 6));
  if (month < 1 || month > 12) return null;
  const lastDayOfMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (day > lastDayOfMonth) return null;
  const resolvedDay = day === 0 ? lastDayOfMonth : day;
  return `${year}-${String(month).padStart(2, '0')}-${String(resolvedDay).padStart(2, '0')}`;
};

/**
 * AIM symbology identifiers (`]C1` for GS1-128, `]d2` for GS1 DataMatrix, `]Q3` for GS1 QR …).
 * Scandit never sends these; a DataWedge profile with "Transmit Code ID" turned on does. `]` is
 * not in GS1 encodable character set 82, so a leading one is never barcode data.
 */
const AIM_IDENTIFIER = /^\][A-Za-z][0-9]/;

/**
 * Printable stand-ins for the GS control character. Advanced Data Formatting rules, and a couple
 * of scanner firmwares, replace the non-printable separator with one of these rather than drop
 * it; treating them as separators is free and turns an unparseable payload into a clean one.
 */
const TEXTUAL_SEPARATORS = /<GS>|\{GS\}|\u241d/g;

/** C0 control characters other than GS — transport noise, never barcode data. */
const STRAY_CONTROLS = /[ --]/g;

/**
 * Put a payload from any scan source into the one shape the parser expects: no transport
 * framing, no terminator, GS as the only separator.
 */
export const normalizeScanPayload = (raw: string): string => {
  if (!raw) return '';
  // Trimmed at both ends: GS1's encodable character set has no space, and a wedge configured with
  // a space or newline suffix would otherwise name a tote " TOTE-1" and create a look-alike.
  let value = raw.trim();

  // ISO 15434 envelope ("[)>" RS "06" GS … RS EOT). Some imagers wrap GS1 data in one; the
  // envelope is framing, and what is inside it is an ordinary element string.
  const envelope = `[)>${RS}06`;
  if (value.startsWith(envelope)) {
    value = value.slice(envelope.length).replace(new RegExp(`${RS}?${EOT}?$`), '');
  }

  value = value.replace(TEXTUAL_SEPARATORS, GS).replace(STRAY_CONTROLS, '');

  if (AIM_IDENTIFIER.test(value) && /^\d/.test(value.slice(3))) value = value.slice(3);
  // A leading FNC1 is structural, not data.
  while (value.startsWith(GS)) value = value.slice(1);
  return value;
};

export type Gs1Element = {
  ai: string;
  value: string;
  /**
   * How this field's end was decided. `inferred` means no separator was present and the boundary
   * was chosen because it made the remainder of the payload validate — see `parseGs1`.
   */
  terminator: 'fixed' | 'separator' | 'end' | 'inferred';
};

export type Gs1Parse = {
  elements: Gs1Element[];
  /** AI → value. A repeated AI keeps its first occurrence. */
  ais: Record<string, string>;
  /** The payload carried at least one FNC1/GS, so the scanner is not stripping separators. */
  separatorsPresent: boolean;
  /** How many field boundaries had to be guessed. Non-zero means the payload was malformed. */
  inferredBoundaries: number;
  score: number;
};

// Scoring drives the *choice between readings*, never whether a payload is accepted, so a
// genuinely misprinted check digit still parses — it just loses to a better-validating rival.
const SCORE_AI_KNOWN = 10;
const SCORE_DATE_OK = 25;
const SCORE_CHECK_DIGIT_OK = 25;
const PENALTY_DATE_BAD = -60;
const PENALTY_CHECK_DIGIT_BAD = -40;
const SCORE_TERMINATED_BY_SEPARATOR = 30;
const SCORE_TERMINATED_BY_END = 10;
const PENALTY_INFERRED_BOUNDARY = -8;
/** Breaks ties toward the longer reading of a variable-length field. */
const LENGTH_TIEBREAK = 0.001;

/** Guessing more boundaries than this means the payload is beyond salvage. */
const MAX_INFERRED_BOUNDARIES = 3;
/** Search-space guard; real element strings settle in a few dozen nodes. */
const MAX_SEARCH_NODES = 50000;

/** Score for one element, or null when this reading is impossible rather than merely unlikely. */
const scoreElement = (spec: AiSpec, element: Gs1Element): number | null => {
  const { value } = element;
  // A numeric AI holding a letter is not a misprint, it is a misread boundary.
  if (spec.numeric && !/^\d*$/.test(value)) return null;

  let score = SCORE_AI_KNOWN;
  if (YYMMDD_AIS.has(spec.ai)) {
    score += gs1DateToIso(value) ? SCORE_DATE_OK : PENALTY_DATE_BAD;
  }

  const checkDigitLength = CHECK_DIGIT_AIS.get(spec.ai);
  if (checkDigitLength !== undefined) {
    score += hasValidGs1CheckDigit(value.slice(0, checkDigitLength))
      ? SCORE_CHECK_DIGIT_OK
      : PENALTY_CHECK_DIGIT_BAD;
  }

  if (element.terminator === 'separator') score += SCORE_TERMINATED_BY_SEPARATOR;
  else if (element.terminator === 'end') score += SCORE_TERMINATED_BY_END;
  else if (element.terminator === 'inferred') score += PENALTY_INFERRED_BOUNDARY;

  if (spec.min !== spec.max) score += value.length * LENGTH_TIEBREAK;
  return score;
};

/**
 * True when this element proves itself arithmetically. Deliberately check digits only: a valid
 * date is not proof, because a numeric rack or tote label of the right length reads as one —
 * `17250131` would otherwise come back as an expiry of 2025-01-31 rather than as a label.
 */
const provesItself = (element: Gs1Element): boolean => {
  const checkDigitLength = CHECK_DIGIT_AIS.get(element.ai);
  if (checkDigitLength === undefined) return false;
  return hasValidGs1CheckDigit(element.value.slice(0, checkDigitLength));
};

/** Reads the AI starting at `pos`, or null when nothing there is a known AI. */
const readAi = (raw: string, pos: number): AiSpec | null => {
  const prefix = raw.slice(pos, pos + 2);
  if (!/^\d{2}$/.test(prefix)) return null;
  const aiLength = AI_LENGTH_BY_PREFIX.get(prefix);
  if (!aiLength) return null;
  const ai = raw.slice(pos, pos + aiLength);
  if (ai.length !== aiLength) return null;
  return AI_SPECS.get(ai) || null;
};

type Reading = { elements: Gs1Element[]; score: number } | null;

/**
 * Parse a normalized payload as a GS1 element string.
 *
 * The happy path is ordinary: fixed-length AIs consume their declared number of characters,
 * variable-length ones run to the next FNC1. What makes this more than a loop is the case where
 * the separators never arrived — a DataWedge profile delivering key events without
 * "Send Control Characters as Events" gives us `01…21…17…10…` as one unbroken run, and a naive
 * reader hands back a serial number with the expiry and lot glued onto the end.
 *
 * So a boundary with nothing after it is treated as a *choice*: run the field to the end of the
 * payload, or cut it short wherever the remainder parses. Both readings are computed and the
 * literal one is preferred unless the guess earns its place. Four rules keep it honest:
 *
 *   1. Guessing is only attempted when the payload contains no separator at all. One real FNC1
 *      anywhere proves the scanner transmits them, and then a field with nothing after it
 *      really is the last field.
 *   2. A cut may only be made in front of a fixed-length numeric AI, so the reading is settled
 *      by a date or a check digit rather than by looking plausible.
 *   3. A cut may not expose an AI the payload has already used. This is what stops a lot number
 *      such as `X17250131` being torn in half at its own "17".
 *   4. A guess must recover at least two AIs the literal reading missed, unless the literal
 *      reading does not exist at all. This is the rule that keeps `(01)(10)ABC17250131` — a
 *      perfectly legal element string, since a trailing variable field needs no FNC1 — from
 *      being re-read as a lot of `ABC` with an expiry after it. A DSCSA pack always carries
 *      GTIN, serial, expiry and lot, so a scanner that drops separators always costs us two.
 *
 * Returns null when the payload is not a GS1 element string, which is the common case for the
 * zone rack labels and tote labels this app also scans.
 */
export const parseGs1 = (payload: string): Gs1Parse | null => {
  const raw = normalizeScanPayload(payload);
  if (!raw) return null;

  const separatorsPresent = raw.includes(GS);
  let allowInference = false;
  let nodes = 0;

  let memo = new Map<number, Reading>();
  const seen = new Set<string>();

  const solve = (pos: number, inferredLeft: number): Reading => {
    if (pos >= raw.length) return { elements: [], score: 0 };
    // Memoize only while no guess has been made; once the path carries `seen` state the answer
    // at a position is no longer independent of how we got there.
    const memoizable = !allowInference || inferredLeft === MAX_INFERRED_BOUNDARIES;
    if (memoizable && memo.has(pos)) return memo.get(pos) as Reading;

    nodes += 1;
    if (nodes > MAX_SEARCH_NODES) return null;

    // A separator sitting where an AI should start is padding between element strings.
    if (raw[pos] === GS) {
      const rest = solve(pos + 1, inferredLeft);
      if (memoizable) memo.set(pos, rest);
      return rest;
    }

    const spec = readAi(raw, pos);
    // An element string never repeats an AI. Enforcing that is what stops a run of digits from
    // tiling into a "valid" parse — `1`×120 is otherwise fifteen consecutive AI 11 dates.
    if (!spec || seen.has(spec.ai)) {
      if (memoizable) memo.set(pos, null);
      return null;
    }

    let best: Reading = null;
    const consider = (element: Gs1Element, nextPos: number, nextInferred: number) => {
      const elementScore = scoreElement(spec, element);
      if (elementScore === null) return;
      seen.add(spec.ai);
      const rest = solve(nextPos, nextInferred);
      seen.delete(spec.ai);
      if (!rest) return;
      const total = elementScore + rest.score;
      const current = best as Reading;
      if (!current || total > current.score) {
        best = { elements: [element, ...rest.elements], score: total };
      }
    };

    const valueStart = pos + spec.ai.length;

    if (spec.min === spec.max) {
      const end = valueStart + spec.min;
      if (end <= raw.length) {
        const value = raw.slice(valueStart, end);
        if (!value.includes(GS)) {
          // A separator after a fixed-length field is redundant but legal; step over it.
          consider(
            { ai: spec.ai, value, terminator: 'fixed' },
            raw[end] === GS ? end + 1 : end,
            inferredLeft
          );
        }
      }
    } else {
      const separatorAt = raw.indexOf(GS, valueStart);

      if (separatorAt >= 0) {
        const value = raw.slice(valueStart, separatorAt);
        if (value.length >= spec.min && value.length <= spec.max) {
          consider(
            { ai: spec.ai, value, terminator: 'separator' },
            separatorAt + 1,
            inferredLeft
          );
        }
      } else {
        const value = raw.slice(valueStart);
        if (value.length >= spec.min && value.length <= spec.max) {
          consider({ ai: spec.ai, value, terminator: 'end' }, raw.length, inferredLeft);
        }

        if (allowInference && inferredLeft > 0) {
          const longest = Math.min(spec.max, raw.length - valueStart - 1);
          for (let length = spec.min; length <= longest; length += 1) {
            const cut = valueStart + length;
            const nextSpec = readAi(raw, cut);
            if (!nextSpec || nextSpec.min !== nextSpec.max || !nextSpec.numeric) continue;
            if (seen.has(nextSpec.ai) || nextSpec.ai === spec.ai) continue;
            consider(
              { ai: spec.ai, value: raw.slice(valueStart, cut), terminator: 'inferred' },
              cut,
              inferredLeft - 1
            );
          }
        }
      }
    }

    if (memoizable) memo.set(pos, best);
    return best;
  };

  // Read it literally first: fixed lengths, and every variable field running to the next
  // separator or to the end of the payload.
  const literal = solve(0, 0);

  // Then, only if the scanner sent no separators at all, see what guessing boundaries would
  // recover. Rule 4 above decides which of the two we believe.
  let result = literal;
  if (!separatorsPresent) {
    allowInference = true;
    memo = new Map<number, Reading>();
    nodes = 0;
    const guessed = solve(0, MAX_INFERRED_BOUNDARIES);
    const gained = (guessed?.elements.length || 0) - (literal?.elements.length || 0);
    if (guessed && (!literal || gained >= 2)) result = guessed;
  }

  if (!result || !result.elements.length) return null;

  // Guard against reading an arbitrary numeric label — a zone code, a tote name — as GS1. Any
  // one of these is enough evidence of real structure: a transmitted separator, two AIs that
  // tile the payload exactly, or a single element with a check digit that comes out right.
  const structured =
    separatorsPresent || result.elements.length > 1 || result.elements.some(provesItself);
  if (!structured) return null;

  const ais: Record<string, string> = {};
  for (const element of result.elements) {
    if (!(element.ai in ais)) ais[element.ai] = element.value;
  }

  return {
    elements: result.elements,
    ais,
    separatorsPresent,
    inferredBoundaries: result.elements.filter((e) => e.terminator === 'inferred').length,
    score: result.score
  };
};

export type ScanKind = 'gs1' | 'sscc' | 'raw';

export type ScanPayload = {
  /** GTIN-14 from AI 01 (or AI 02 on a logistics unit), or null. */
  gtin: string | null;
  /**
   * AI 21 serial, or — for a pallet label — the 17 meaningful SSCC digits (AI and check digit
   * removed) that the backend resolves by concatenation match.
   */
  serialNumber: string | null;
  lot: string | null;
  /** ISO calendar date (`YYYY-MM-DD`) from AI 17, or null. */
  expiration: string | null;
  /** AI 30 / AI 37 piece count, or null. */
  count: string | null;
  /** Full 18-digit SSCC from AI 00, or null. */
  sscc: string | null;
  /** What the payload turned out to be. `raw` covers zone rack labels, tote labels, anything else. */
  kind: ScanKind;
  /** Payload as received, after transport normalization. Zone/tote lookups match on this. */
  text: string;
  /** Set when field boundaries had to be guessed because the scanner dropped the separators. */
  separatorsMissing: boolean;
  ais: Record<string, string>;
};

/** An SSCC element string as a linear scanner delivers it: AI 00 plus 18 digits. */
const BARE_SSCC = /^00(\d{18})$/;

/**
 * The one entry point every scan source funnels through. Always returns a payload — an
 * unrecognised string is reported as `kind: 'raw'` rather than as a failure, because the same
 * scanner reads zone rack labels and tote labels that are not GS1 at all.
 */
export const parseScanPayload = (raw: string): ScanPayload => {
  const text = normalizeScanPayload(raw || '');
  const empty: ScanPayload = {
    gtin: null,
    serialNumber: null,
    lot: null,
    expiration: null,
    count: null,
    sscc: null,
    kind: 'raw',
    text,
    separatorsMissing: false,
    ais: {}
  };
  if (!text) return empty;

  const parsed = parseGs1(text);
  if (!parsed) {
    // A pallet label whose check digit is wrong still has to scan — warehouses do run mislabelled
    // pallets, and refusing them here would be a change in behaviour, not a safety feature.
    const bare = BARE_SSCC.exec(text);
    if (!bare) return empty;
    return {
      ...empty,
      serialNumber: bare[1].slice(0, 17),
      sscc: bare[1],
      kind: 'sscc',
      ais: { '00': bare[1] }
    };
  }

  const { ais } = parsed;
  const sscc = ais['00'] || null;
  const gtin = ais['01'] || ais['02'] || null;
  const serial = ais['21'] || null;

  return {
    // A pallet label carries an SSCC and nothing to pick, so it is reported as a container
    // rather than as a product: no GTIN, and the SSCC standing in as the serial the way the
    // manual SSCC entry form already does.
    gtin: serial || !sscc ? gtin : null,
    serialNumber: serial || (sscc ? sscc.slice(0, 17) : null),
    lot: ais['10'] || null,
    expiration: gs1DateToIso(ais['17']),
    count: ais['30'] || ais['37'] || null,
    sscc,
    kind: sscc && !serial ? 'sscc' : 'gs1',
    text,
    separatorsMissing: parsed.inferredBoundaries > 0,
    ais
  };
};
